import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { useAuth } from './authContext'
import { useNavigate, useParams } from 'react-router-dom'
import { addNewSavedMessage, deleteSavedMessage, getAllFlags, getSavedMessages, updateSavedMessage } from '../schemas/Chat'
import { uploadFile } from '../schemas/File'
import { formatSecondsToTime, maskPhone } from '../utils/formatter'
import { toast } from 'react-toastify'
import { HiOutlineTemplate } from "react-icons/hi"
import { MdOutlineContactPage, MdOutlineFormatListBulleted, MdOutlineGifBox, MdOutlineImage, MdOutlineInsertDriveFile, MdOutlineLocationOn, MdOutlineMicNone, MdOutlineNote, MdOutlineVideocam } from "react-icons/md"
import api from '../services/api'
import ModalStartChat from '../components/Modal/Chat/ModalStartChat'
import Loading from '../pages/layouts/Loading'

const WebSocketChatContext = createContext()

const WebSocketChatProvider = ({ children }) => {
    const [chatInboxes, setChatInboxes] = useState([])
    const [chatFlags, setChatFlags] = useState([])
    const [chatSavedMessages, setChatSavedMessages] = useState([])
    const [connect, setConnect] = useState(true)
    const [dataStartChat, setDataStartChat] = useState(false)
    const [showModalStartChat, setShowModalStartChat] = useState(false)
    const [restrictedConversationAccess, setRestrictedConversationAccess] = useState(null)
    const [loading, setLoading] = useState(false)
    const [awaitRequest, setAwaitRequest] = useState(false)

    const { token, modules, verifyIfModuleIsActive, verifyIfUserHasPermission } = useAuth()
    const { account_id } = useParams()
    const navigate = useNavigate()
    const wsUrl = `${process.env.REACT_APP_SERVER_HOST_CHAT_WS}`

    const socketOptions = useMemo(() => ({
        onMessage: (event) => {
            if (event.data === 'ping') {
                sendMessage("pong")
            }
        },
        queryParams: { token, account_id },
        share: true,
        shouldReconnect: () => Boolean(token && account_id),
        reconnectInterval: (attemptNumber) => {
            const jitter = Math.floor(Math.random() * 1000)
            return Math.min(1000 * (2 ** attemptNumber), 15000) + jitter
        },
    }), [token, account_id])

    const { lastJsonMessage, readyState, sendMessage, sendJsonMessage } = useWebSocket(wsUrl, socketOptions, connect && Boolean(token && account_id))

    useEffect(() => {
        if (!connect) {
            setConnect(true)
        }
    }, [connect])

    useEffect(() => {
        loadInitialData(account_id)
    }, [account_id, modules])

    useEffect(() => {
        if (lastJsonMessage?.event === "UPDATE_INBOX") {
            updateChatInbox(lastJsonMessage.content)
        }
    }, [lastJsonMessage])

    const loadInitialData = async () => {
        const moduleIsActive = verifyIfModuleIsActive("CHAT")
        const userHasPermission = await verifyIfUserHasPermission("chat", account_id)
        if (account_id && moduleIsActive && userHasPermission) {
            setTimeout(() => {
                getAllChatInboxes()
            }, 5000)
        }
    }
    const getAllSavedMessages = async (reload = false) => {
        if (chatSavedMessages.length <= 0 || reload) {
            getSavedMessages().then(messages => {
                setChatSavedMessages(messages)
            }).catch(err => {
                toast.error(err?.response?.data?.message)
            })
        }
    }
    const getAllChatInboxes = () => {
        api.get("/chat/inboxes").then(response => {
            const inboxes = response.data?.data || []
            setChatInboxes(inboxes)
        }).catch(err => toast.error(err?.response?.data?.message))
    }
    const updateChatInbox = (content = {}) => {
        const inboxId = content?._id
        if (!inboxId) return

        setChatInboxes(previousValue => previousValue.map(inbox => {
            if (inbox?._id !== inboxId) {
                return inbox
            }

            return {
                ...inbox,
                ...content,
                metadata: {
                    ...(inbox?.metadata || {}),
                    ...(content?.metadata || {})
                }
            }
        }))
    }
    const getAllChatFlags = () => {
        getAllFlags().then(flags => {
            setChatFlags(flags)
        }).catch(err => toast.error(err?.response?.data?.message))
    }
    const getContactByContactDataAndPlatform = (contactData, platform) => {
        const identifier = getIdentifierContactByPlatform(contactData, platform)
        const isMetaMessaging = platform === "messenger" || platform === "direct"
        const name = (platform === "whatsapp" || platform === "whatsapp_api" || isMetaMessaging) ? contactData?.display_name || contactData?.name || contactData?.number || contactData?.display_identifier : ""
        const original_name = contactData?.original_name || ""
        const picture = (platform === "whatsapp" || platform === "whatsapp_api" || isMetaMessaging) ? contactData?.picture : ""
        const identifier_label = contactData?.display_identifier_label || ""
        const additional_fields = Array.isArray(contactData?.additional_fields) ? contactData.additional_fields : []
        const profile_url = contactData?.profile_url || ""
        return { name, original_name, picture, identifier, identifier_label, additional_fields, profile_url }
    }
    const getIdentifierContactByPlatform = (contact, platform) => {
        let identifier = ""
        if (platform === "whatsapp" || platform === "whatsapp_api") {
            identifier = contact?.display_identifier_label === "Telefone" ? maskPhone(contact?.display_identifier) : contact?.display_identifier || ""
        } else if (platform === "messenger" || platform === "direct") {
            identifier = contact?.display_identifier || ""
        }
        return identifier
    }
    const getIconMessageByType = (type) => {
        switch (type) {
            case "image":
                return <MdOutlineImage />
            case "audio":
                return <MdOutlineMicNone />
            case "video":
                return <MdOutlineVideocam />
            case "document":
                return <MdOutlineInsertDriveFile />
            case "location":
                return <MdOutlineLocationOn />
            case "contact":
                return <MdOutlineContactPage />
            case "gif":
                return <MdOutlineGifBox />
            case "sticker":
                return <MdOutlineNote />
            case "poll":
                return <MdOutlineFormatListBulleted />
            case "list":
                return <MdOutlineFormatListBulleted />
            case "template":
                return <HiOutlineTemplate />
            default:
                return ""
        }
    }
    const getContentByMessage = (message) => {
        const text = message?.content?.text
        if (!text) {
            switch (message?.type) {
                case "image":
                    return "Imagem"
                case "audio":
                    return message?.content?.seconds > 0 ? formatSecondsToTime(message.content.seconds, false) : "Áudio"
                case "video":
                    return "Vídeo"
                case "document":
                    return message?.content?.name || "Documento"
                case "location":
                    return "Localização"
                case "contact":
                    return "Contato"
                case "gif":
                    return "GIF"
                case "sticker":
                    return "Figurinha"
                case "poll":
                    return "Enquete"
                case "list":
                    return "Lista"
                case "template":
                    return "Template"
                default:
                    return ""
            }
        }
        return text
    }
    const handleReconnect = useCallback(() => {
        setConnect(false)
    }, [])
    const handleOpenModalStartChat = (data) => {
        setShowModalStartChat(true)
        setDataStartChat(data)
        setRestrictedConversationAccess(null)
    }
    const handleCloseModalStartChat = () => {
        setShowModalStartChat(false)
        setDataStartChat({})
        setRestrictedConversationAccess(null)
    }
    const handleClearRestrictedConversationAccess = () => {
        setRestrictedConversationAccess(null)
    }
    const startConversationByInboxAndContactId = async (message) => {
        setLoading(true)
        try {
            const response = await api.post("/chat/conversations/start", message)
            const conversation = response.data?.data
            handleCloseModalStartChat()
            if (conversation?._id) {
                navigate(`/${account_id}/chat/${conversation._id}`)
            }
        } catch (err) {
            if (err?.response?.status === 409 && err?.response?.data?.data?.conversation_id) {
                setRestrictedConversationAccess(err.response.data.data)
                toast.info(err?.response?.data?.message || "Essa conversa já existe com outro responsável.")
                return
            }
            toast.error(err?.response?.data?.message || "Não foi possível iniciar a conversa.")
        } finally {
            setLoading(false)
        }
    }
    const requestConversationAccess = async () => {
        const conversationId = restrictedConversationAccess?.conversation_id
        if (!conversationId || restrictedConversationAccess?.access_request_status === "pending") return

        setLoading(true)
        try {
            const response = await api.post(`/chat/conversations/${conversationId}/access-requests`, {
                account_id,
                reason: "Solicitado a partir da tela de iniciar conversa.",
            })
            toast.success(response.data?.message)
            setRestrictedConversationAccess(previousValue => ({
                ...previousValue,
                access_request_status: "pending",
                access_request: response.data?.data,
            }))
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível solicitar acesso.")
        } finally {
            setLoading(false)
        }
    }
    const handleAddOrEditSavedMessage = async (message, edit = false, cb_fn = () => { }) => {
        setAwaitRequest(true)
        if (message?.content?.file || message?.content?.audio) {
            try {
                const file = message?.content?.file?.length > 0 ? message?.content?.file[0] : message?.content?.audio
                const { keyFileS3, keyFileS3Thumb, file_id } = await uploadFile(file, { resize: message?.type === "image", platform: "whatsapp" })
                message.content.keyFileS3 = keyFileS3
                message.content.keyFileS3Thumb = keyFileS3Thumb
                message.content.file_id = file_id
                message.content.audio = undefined
                message.content.file = undefined
            } catch (err) {
                toast.error(err?.response?.data?.message)
            }
        }
        if (edit) {
            updateSavedMessage(message).then(response => {
                const newMessage = response.data?.data || {}
                updateDataSavedMessage(newMessage)
                toast.success(response?.data?.message)
                cb_fn()
            }).catch(err => toast.error(err?.response?.data?.message)).finally(() => {
                setAwaitRequest(false)
            })
        } else {
            addNewSavedMessage(message).then(response => {
                const newMessage = response.data?.data || {}
                updateDataSavedMessage(newMessage)
                toast.success(response?.data?.message)
                cb_fn()
            }).catch(err => toast.error(err?.response?.data?.message)).finally(() => {
                setAwaitRequest(false)
            })
        }
    }
    const hanleDeleteSavedMessage = (message_id, cb_fn = () => { }) => {
        setAwaitRequest(true)
        deleteSavedMessage(message_id).then(response => {
            removeSavedMessage(message_id)
            toast.success(response?.data?.message)
            cb_fn()
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => {
            setAwaitRequest(false)
        })
    }
    const updateDataSavedMessage = (newMessage) => {
        setChatSavedMessages(previousValue => {
            const messageExists = previousValue?.find(message => message?._id === newMessage?._id)
            if (messageExists) {
                return previousValue?.map(message => {
                    if (message?._id === newMessage?._id) {
                        return newMessage
                    }
                    return message
                })
            } else {
                return [...previousValue, newMessage]
            }
        })
    }
    const removeSavedMessage = (message_id) => {
        setChatSavedMessages(previousValue => previousValue?.filter(message => message?._id !== message_id))
    }

    const connectedInboxes = useMemo(() => {
        return chatInboxes
            .filter(inbox => inbox.status === "connected")
            .map(inbox => ({
                id: inbox._id,
                value: inbox.name
            }))
    }, [chatInboxes])

    return (
        <WebSocketChatContext.Provider value={{ lastJsonMessage, readyState, sendJsonMessage, handleReconnect, ReadyState, chatInboxes, getAllChatInboxes, chatFlags, getAllChatFlags, chatSavedMessages, getAllSavedMessages, getContactByContactDataAndPlatform, getIconMessageByType, getContentByMessage, handleOpenModalStartChat, handleCloseModalStartChat, startConversationByInboxAndContactId, handleAddOrEditSavedMessage, hanleDeleteSavedMessage }}>
            {children}
            {showModalStartChat &&
                <ModalStartChat
                    value={dataStartChat}
                    inboxes={connectedInboxes}
                    restrictedAccess={restrictedConversationAccess}
                    onSubmit={startConversationByInboxAndContactId}
                    onRequestAccess={requestConversationAccess}
                    onClearRestrictedAccess={handleClearRestrictedConversationAccess}
                    closeModal={handleCloseModalStartChat}
                />
            }
            {((showModalStartChat && loading) || awaitRequest) && <Loading fullPage={true} />}
        </WebSocketChatContext.Provider>
    )
}

const useWebSocketChatContext = () => {
    return useContext(WebSocketChatContext)
}

export { useWebSocketChatContext }

export default WebSocketChatProvider
